import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Checkbox, Tooltip, Space } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const validateFieldLabel = (label) => {
  if (!/^{{[a-z0-9_]+}}$/.test(label)) {
    return 'Label must be in snake case, enclosed in double curly braces, with only lowercase letters and numbers.';
  }
  if (label.length > 34) { // Four extra characters for the curly braces
    return 'Label must be 30 characters or fewer (excluding curly braces).';
  }
  return '';
};

const validateDocumentType = (type) => {
  const allowedTypes = ['Test Script', 'Test Plan', 'Requirements', 'Specifications'];
  if (!allowedTypes.includes(type)) {
    return 'Type must be one of the following: Test Script, Test Plan, Requirements, Specifications.';
  }
  return '';
};

const validateFieldName = (name) => {
  if (!/^[A-Za-z0-9_.\- ]+$/.test(name)) {
    return 'Name may include letters, numbers, underscores, hyphens, periods, and spaces.';
  }
  if (name.startsWith('.') || name.endsWith('.')) {
    return 'Name must not start or end with a period.';
  }
  if (name.length > 30) {
    return 'Name must be 30 characters or fewer.';
  }
  return '';
};

function EditMetadataDialog({ open, metadata, onClose, onSave }) {
  const [form] = Form.useForm();
  const [errors, setErrors] = useState({
    documentType: '',
    fieldLabel: '',
    fieldName: '',
  });

  useEffect(() => {
    // When the dialog opens, populate the form with metadata
    if (metadata && open) {
      form.setFieldsValue({
        documentType: metadata.document_type,
        fieldLabel: metadata.field_label,
        fieldName: metadata.field_name,
        dataType: metadata.data_type,
        isRequired: metadata.required,
        order: metadata.order,
        isNameField: metadata.is_name_field,
        options: metadata.options,
      });
      setErrors({
        documentType: '',
        fieldLabel: '',
        fieldName: '',
      });
    }
  }, [metadata, open, form]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      // Additional custom validation
      const fieldLabelErrorMsg = validateFieldLabel(values.fieldLabel);
      const documentTypeErrorMsg = validateDocumentType(values.documentType);
      const fieldNameErrorMsg = validateFieldName(values.fieldName);

      if (fieldLabelErrorMsg || documentTypeErrorMsg || fieldNameErrorMsg) {
        setErrors({
          fieldLabel: fieldLabelErrorMsg,
          documentType: documentTypeErrorMsg,
          fieldName: fieldNameErrorMsg,
        });
        return;
      }

      setErrors({
        documentType: '',
        fieldLabel: '',
        fieldName: '',
      });

      onSave({
        id: metadata.id,
        document_type: values.documentType,
        field_label: values.fieldLabel,
        field_name: values.fieldName,
        data_type: values.dataType,
        required: values.isRequired,
        order: values.order,
        is_name_field: values.isNameField,
        options: values.options,
      });
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSaveClick}
      title="Edit Metadata"
      okText="Save"
      cancelText="Cancel"
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={
            <Space>
              <span>Document Type</span>
              <Tooltip title="Allowed Types: Test Script, Test Plan, Requirements, Specifications.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="documentType"
          validateStatus={errors.documentType ? 'error' : ''}
          help={errors.documentType}
          rules={[{ required: true, message: 'Please enter document type' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>Field Label</span>
              <Tooltip title="Format: Snake case enclosed in {{ }}. Only lowercase letters and numbers, up to 30 characters.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="fieldLabel"
          validateStatus={errors.fieldLabel ? 'error' : ''}
          help={errors.fieldLabel}
          rules={[{ required: true, message: 'Please enter field label' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>Field Name</span>
              <Tooltip title="Include letters, numbers, underscore (_), hyphen (-), and period (.). Up to 30 characters.">
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          }
          name="fieldName"
          validateStatus={errors.fieldName ? 'error' : ''}
          help={errors.fieldName}
          rules={[{ required: true, message: 'Please enter field name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Data Type"
          name="dataType"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="isRequired"
          valuePropName="checked"
        >
          <Checkbox>Required</Checkbox>
        </Form.Item>

        <Form.Item
          label="Order"
          name="order"
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="isNameField"
          valuePropName="checked"
        >
          <Checkbox>Is Name Field</Checkbox>
        </Form.Item>

        <Form.Item
          label="Options (JSON)"
          name="options"
        >
          <TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default EditMetadataDialog;
